// ============================================
// Chat System - Direct Messaging & Group Chats
// ============================================

import { supabase } from './client';
import type {
  DMConversationWithDetails,
  GroupChatWithDetails,
  MessageWithSender,
  SendMessageParams,
  CreateGroupChatParams,
  BlockedUser,
  TypingUser,
  ChatNotificationPreferences,
  GroupChatMemberWithProfile,
  AttachmentUploadResult,
} from '@/types/chat';

// ============================================
// Anonymous Group Chat Functions
// ============================================

/**
 * Send a reply from staff to an anonymous support chat (so the anonymous user sees it in real time)
 * DEPRECATED: Use sendAnonymousGroupMessage instead
 */
export const sendAnonymousChatReply = async (
  anonymousSessionId: string,
  replyMessage: string
): Promise<void> => {
  const { error } = await supabase.rpc('send_anonymous_chat_reply', {
    p_anonymous_session_id: anonymousSessionId,
    p_reply_message: replyMessage,
    p_support_store_id: '30e79df7-81e8-4c5f-a825-602e5899d961',
  });
  if (error) throw error;
};

/**
 * Get or create a group chat for an anonymous user
 * Returns the group_chat_id where anonymous user + store staff chat
 */
export const getOrCreateAnonymousGroupChat = async (
  anonymousSessionId: string,
  storeId: string
): Promise<{ group_chat_id: string; is_new: boolean }> => {
  if (!anonymousSessionId || !storeId) {
    throw new Error(`Missing parameters: anonymousSessionId=${anonymousSessionId}, storeId=${storeId}`);
  }
  
  const { data, error } = await supabase.rpc('get_or_create_anonymous_group_chat', {
    p_anonymous_session_id: anonymousSessionId,
    p_store_id: storeId,
  });
  
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Failed to create/get anonymous group chat');
  
  return {
    group_chat_id: data[0].group_chat_id,
    is_new: data[0].is_new,
  };
};

/**
 * Get the group chat ID associated with an anonymous session
 */
export const getGroupChatFromAnonymousSession = async (
  anonymousSessionId: string
): Promise<string | null> => {
  const { data, error } = await supabase.rpc('get_group_chat_from_anonymous_session', {
    p_anonymous_session_id: anonymousSessionId,
  });
  if (error) throw error;
  return (data as string) || null;
};

/**
 * Send a message to an anonymous support group chat
 * The message will be visible to all store staff in the group
 */
export const sendAnonymousGroupMessage = async (
  groupChatId: string,
  messageContent: string,
  messageType: string = 'text'
): Promise<string> => {
  return sendGroupMessage({
    group_id: groupChatId,
    content: messageContent,
    type: messageType,
  });
};

/**
 * Send message as anonymous user to group chat
 * Used when anonymous user sends message (sender_id = NULL, anonymous_session_id in metadata)
 */
export const sendAnonymousUserGroupMessage = async (
  groupChatId: string,
  anonymousSessionId: string,
  messageContent: string
): Promise<string> => {
  const { data, error } = await supabase.rpc('send_anonymous_group_message', {
    p_group_chat_id: groupChatId,
    p_anonymous_session_id: anonymousSessionId,
    p_message_content: messageContent,
  });
  if (error) throw error;
  return (data as string) || '';
};

/**
 * Send a system message to a group chat (e.g., "User joined the chat")
 */
export const sendSystemGroupMessage = async (
  groupChatId: string,
  messageContent: string
): Promise<string> => {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      group_chat_id: groupChatId,
      sender_id: null,
      content: messageContent,
      type: 'text',
      metadata: { is_system: true },
    })
    .select('id')
    .single();
  
  if (error) throw error;
  return (data?.id as string) || '';
};

/**
 * Get messages for anonymous user from their group chat
 * Allows anonymous users to read messages without authentication
 */
export const getAnonymousGroupMessages = async (
  anonymousSessionId: string,
  limit = 50,
  offset = 0
): Promise<MessageWithSender[]> => {
  console.log('[getAnonymousGroupMessages] Fetching for session:', anonymousSessionId);
  
  // Validate parameters
  if (!anonymousSessionId) {
    console.error('[getAnonymousGroupMessages] Missing anonymousSessionId');
    return [];
  }
  
  if (typeof anonymousSessionId !== 'string') {
    console.error('[getAnonymousGroupMessages] anonymousSessionId is not a string:', typeof anonymousSessionId);
    return [];
  }
  
  console.log('[getAnonymousGroupMessages] Calling RPC with:', {
    p_anonymous_session_id: anonymousSessionId,
    p_limit: limit,
    p_offset: offset,
  });
  
  const { data, error } = await supabase.rpc('get_anonymous_group_messages', {
    p_anonymous_session_id: anonymousSessionId,
    p_limit: limit,
    p_offset: offset,
  });
  
  console.log('[getAnonymousGroupMessages] Response:', { data, error });
  
  if (error) {
    console.error('[getAnonymousGroupMessages] Full error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
    });
    throw error;
  }
  
  if (!data || data.length === 0) {
    console.log('[getAnonymousGroupMessages] No messages found');
    return [];
  }
  
  // Transform to MessageWithSender format
  return (data || []).map((msg: any) => ({
    id: msg.id,
    content: msg.content,
    message: msg.content,
    sender_id: msg.sender_id,
    sender_username: msg.metadata?.is_anonymous ? 'Tú' : 'Support',
    sender_display_name: msg.metadata?.is_anonymous ? 'Tú' : 'Support',
    sender_avatar_url: null,
    type: msg.type,
    created_at: msg.created_at,
    updated_at: msg.updated_at,
    is_edited: msg.is_edited,
    is_deleted: msg.is_deleted,
    metadata: msg.metadata,
    dm_conversation_id: null,
    group_chat_id: msg.group_chat_id,
    attachment_url: msg.attachment_url,
    attachment_type: msg.attachment_type,
    attachment_name: msg.attachment_name,
    attachment_size: msg.attachment_size,
    is_read: true,
  })) as MessageWithSender[];
};

/**
 * Migrate anonymous chat to authenticated user's account
 * When user logs in, they join the existing group chat
 */
export const migrateAnonymousChatOnLogin = async (
  userId: string,
  anonymousSessionId: string
): Promise<void> => {
  const { data, error } = await supabase.rpc('migrate_anonymous_chat_on_login', {
    p_user_id: userId,
    p_anonymous_session_id: anonymousSessionId,
  });
  if (error) throw error;
};

// ============================================
// Direct Messaging System - Chat API Service
// ============================================

// ============================================
// DM Conversations
// ============================================

/**
 * Get or create a DM conversation with another user
 */
export const getOrCreateDMConversation = async (otherUserId: string): Promise<string> => {
  const { data, error } = await supabase.rpc('get_or_create_dm_conversation', {
    other_user_id: otherUserId,
  });

  if (error) throw error;
  return data as string;
};

/**
 * Get all DM conversations for the current user
 */
export const getUserDMConversations = async (): Promise<DMConversationWithDetails[]> => {
  const { data, error } = await supabase.rpc('get_user_dm_conversations');

  if (error) throw error;
  return data as DMConversationWithDetails[];
};

/**
 * Get messages from a DM conversation
 */
export const getDMMessages = async (
  conversationId: string,
  limit = 50,
  offset = 0
): Promise<MessageWithSender[]> => {
  const { data, error } = await supabase.rpc('get_dm_messages', {
    conversation_id: conversationId,
    limit_count: limit,
    offset_count: offset,
  });

  if (!error && Array.isArray(data)) {
    return data as MessageWithSender[];
  }

  console.warn('[chat] get_dm_messages RPC failed, using table fallback', {
    conversationId,
    error,
  });

  const { data: fallbackData, error: fallbackError } = await supabase
    .from('messages')
    .select(`
      id,
      sender_id,
      type,
      content,
      attachment_url,
      attachment_type,
      attachment_name,
      attachment_size,
      created_at,
      updated_at,
      is_edited,
      is_deleted,
      dm_conversation_id,
      group_chat_id,
      metadata
    `)
    .eq('dm_conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + limit - 1);

  if (fallbackError) throw fallbackError;

  const senderIds = Array.from(
    new Set((fallbackData || []).map((m: any) => m.sender_id).filter(Boolean))
  );
  const { data: profilesData } = senderIds.length
    ? await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', senderIds)
    : { data: [] as any[] };

  const profilesById = new Map(
    (profilesData || []).map((p: any) => [p.id, p])
  );

  return (fallbackData || []).map((msg: any) => ({
    id: msg.id,
    sender_id: msg.sender_id,
    type: msg.type,
    content: msg.content,
    attachment_url: msg.attachment_url,
    attachment_type: msg.attachment_type,
    attachment_name: msg.attachment_name,
    attachment_size: msg.attachment_size,
    created_at: msg.created_at,
    updated_at: msg.updated_at,
    is_edited: msg.is_edited,
    is_deleted: msg.is_deleted,
    dm_conversation_id: msg.dm_conversation_id,
    group_chat_id: msg.group_chat_id,
    metadata: msg.metadata,
    sender_username: profilesById.get(msg.sender_id)?.username || 'Unknown',
    sender_display_name: profilesById.get(msg.sender_id)?.display_name ?? null,
    sender_avatar_url: profilesById.get(msg.sender_id)?.avatar_url ?? null,
  })) as MessageWithSender[];
};

/**
 * Send a DM message
 */
export const sendDMMessage = async (params: SendMessageParams): Promise<string> => {
  if (!params.recipient_id) throw new Error('recipient_id is required for DM messages');

  const { data, error } = await supabase.rpc('send_dm_message', {
    recipient_id: params.recipient_id,
    message_type: params.type,
    message_content: params.content,
    attachment_url: params.attachment_url,
    attachment_type: params.attachment_type,
    attachment_name: params.attachment_name,
    attachment_size: params.attachment_size,
  });

  if (error) throw error;
  return data as string;
};

// ============================================
// Group Chats
// ============================================

/**
 * Upload a group chat avatar to public avatars bucket
 */
export const uploadGroupChatAvatar = async (file: File): Promise<string> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 8);
  const extension = file.name.split('.').pop() || 'jpg';
  const filePath = `public/${user.user.id}/group-chats/${timestamp}-${random}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data: publicUrlData } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath);

  return publicUrlData.publicUrl;
};

/**
 * Create a new group chat
 */
export const createGroupChat = async (params: CreateGroupChatParams): Promise<string> => {
  const { data, error } = await supabase.rpc('create_group_chat', {
    group_name: params.name,
    group_description: params.description,
    member_ids: params.member_ids,
  });

  if (error) throw error;

  if (params.avatar_url) {
    const { error: updateError } = await supabase
      .from('group_chats')
      .update({ avatar_url: params.avatar_url })
      .eq('id', data);

    if (updateError) throw updateError;
  }

  return data as string;
};

/**
 * Get all group chats for the current user
 */
export const getUserGroupChats = async (): Promise<GroupChatWithDetails[]> => {
  const { data, error } = await supabase.rpc('get_user_group_chats');

  if (error) throw error;
  return data as GroupChatWithDetails[];
};

export const setGroupChatStarter = async (
  groupId: string,
  starterUserId?: string
): Promise<boolean> => {
  const { data, error } = await supabase.rpc('set_group_chat_starter', {
    p_group_id: groupId,
    p_starter_user_id: starterUserId || null,
  });

  if (error) throw error;
  return Boolean(data);
};

/**
 * Get messages from a group chat
 */
export const getGroupMessages = async (
  groupId: string,
  limit = 50,
  offset = 0
): Promise<MessageWithSender[]> => {
  const { data, error } = await supabase.rpc('get_group_messages', {
    group_id: groupId,
    limit_count: limit,
    offset_count: offset,
  });

  if (!error && Array.isArray(data)) {
    return data as MessageWithSender[];
  }

  console.warn('[chat] get_group_messages RPC failed, using table fallback', {
    groupId,
    error,
  });

  const { data: fallbackData, error: fallbackError } = await supabase
    .from('messages')
    .select(`
      id,
      sender_id,
      type,
      content,
      attachment_url,
      attachment_type,
      attachment_name,
      attachment_size,
      created_at,
      updated_at,
      is_edited,
      is_deleted,
      dm_conversation_id,
      group_chat_id,
      metadata
    `)
    .eq('group_chat_id', groupId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + limit - 1);

  if (fallbackError) throw fallbackError;

  const senderIds = Array.from(
    new Set((fallbackData || []).map((m: any) => m.sender_id).filter(Boolean))
  );
  const { data: profilesData } = senderIds.length
    ? await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', senderIds)
    : { data: [] as any[] };

  const profilesById = new Map(
    (profilesData || []).map((p: any) => [p.id, p])
  );

  return (fallbackData || []).map((msg: any) => ({
    id: msg.id,
    sender_id: msg.sender_id,
    type: msg.type,
    content: msg.content,
    attachment_url: msg.attachment_url,
    attachment_type: msg.attachment_type,
    attachment_name: msg.attachment_name,
    attachment_size: msg.attachment_size,
    created_at: msg.created_at,
    updated_at: msg.updated_at,
    is_edited: msg.is_edited,
    is_deleted: msg.is_deleted,
    dm_conversation_id: msg.dm_conversation_id,
    group_chat_id: msg.group_chat_id,
    metadata: msg.metadata,
    sender_username: profilesById.get(msg.sender_id)?.username || 'Unknown',
    sender_display_name: profilesById.get(msg.sender_id)?.display_name ?? null,
    sender_avatar_url: profilesById.get(msg.sender_id)?.avatar_url ?? null,
  })) as MessageWithSender[];
};

/**
 * Send a group message
 */
export const sendGroupMessage = async (params: SendMessageParams): Promise<string> => {
  if (!params.group_id) throw new Error('group_id is required for group messages');

  const { data, error } = await supabase.rpc('send_group_message', {
    group_id: params.group_id,
    message_type: params.type,
    message_content: params.content,
    attachment_url: params.attachment_url,
    attachment_type: params.attachment_type,
    attachment_name: params.attachment_name,
    attachment_size: params.attachment_size,
  });

  if (error) throw error;
  return data as string;
};

/**
 * Get group members
 */
export const getGroupMembers = async (groupId: string): Promise<GroupChatMemberWithProfile[]> => {
  const { data, error } = await supabase
    .from('group_chat_members')
    .select(`
      id,
      user_id,
      role,
      joined_at,
      profiles:user_id (
        username,
        avatar_url
      )
    `)
    .eq('group_chat_id', groupId);

  if (error) throw error;

  return (data || []).map((member: any) => ({
    id: member.id,
    user_id: member.user_id,
    username: member.profiles?.username || 'Unknown',
    avatar_url: member.profiles?.avatar_url,
    role: member.role,
    joined_at: member.joined_at,
  }));
};

/**
 * Add a member to a group
 */
export const addGroupMember = async (groupId: string, userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('add_group_member', {
    group_id: groupId,
    new_member_id: userId,
  });

  if (error) throw error;
  return data as boolean;
};

/**
 * Remove a member from a group
 */
export const removeGroupMember = async (groupId: string, userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('remove_group_member', {
    group_id: groupId,
    member_id: userId,
  });

  if (error) throw error;
  return data as boolean;
};

/**
 * Leave a group
 */
export const leaveGroup = async (groupId: string): Promise<boolean> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  return removeGroupMember(groupId, user.user.id);
};

/**
 * Create or get an active invite token for a group (admin only)
 */
export const createOrGetGroupInviteToken = async (groupId: string): Promise<string> => {
  const { data, error } = await supabase.rpc('create_or_get_group_invite_token', {
    group_id: groupId,
  });

  if (error) throw error;
  return String(data || '');
};

/**
 * Join a group using an invite token. Returns the joined group id.
 */
export const joinGroupByInviteToken = async (inviteToken: string): Promise<string> => {
  const { data, error } = await supabase.rpc('join_group_by_invite_token', {
    p_invite_token: inviteToken,
  });

  if (error) throw error;
  return String(data || '');
};

// ============================================
// Message Operations
// ============================================

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (messageIds: string[]): Promise<number> => {
  const { data, error } = await supabase.rpc('mark_messages_as_read', {
    message_ids: messageIds,
  });

  if (error) throw error;
  return data as number;
};

/**
 * Edit a message
 */
export const editMessage = async (messageId: string, newContent: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('edit_message', {
    message_id: messageId,
    new_content: newContent,
  });

  if (error) throw error;
  return data as boolean;
};

/**
 * Delete a message (soft delete)
 */
export const deleteMessage = async (messageId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('delete_message', {
    message_id: messageId,
  });

  if (error) throw error;
  return data as boolean;
};

// ============================================
// User Blocking
// ============================================

/**
 * Block a user
 */
export const blockUser = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('block_user', {
    blocked_user_id: userId,
  });

  if (error) throw error;
  return data as boolean;
};

/**
 * Unblock a user
 */
export const unblockUser = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('unblock_user', {
    blocked_user_id: userId,
  });

  if (error) throw error;
  return data as boolean;
};

/**
 * Get blocked users
 */
export const getBlockedUsers = async (): Promise<BlockedUser[]> => {
  const { data, error } = await supabase.rpc('get_blocked_users');

  if (error) throw error;
  return data as BlockedUser[];
};

/**
 * Check if a user is blocked
 */
export const isUserBlocked = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('is_user_blocked', {
    other_user_id: userId,
  });

  if (error) throw error;
  return data as boolean;
};

// ============================================
// Spam Reporting
// ============================================

/**
 * Flag a user for spam
 */
export const flagUserForSpam = async (userId: string, reason?: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('flag_user_for_spam', {
    flagged_user_id: userId,
    spam_reason: reason,
  });

  if (error) throw error;
  return data as boolean;
};

// ============================================
// User Presence
// ============================================

/**
 * Update user presence (online/offline/away)
 */
export const updateUserPresence = async (
  status: 'online' | 'offline' | 'away',
  typingInDm?: string,
  typingInGroup?: string
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('update_user_presence', {
      new_status: status,
      typing_dm: typingInDm || null,
      typing_group: typingInGroup || null,
    });

    if (error) {
      console.debug('updateUserPresence RPC error:', error);
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      if (!userId) return false;

      const { error: fallbackError } = await supabase
        .from('user_presence')
        .upsert(
          {
            user_id: userId,
            status,
            typing_in_dm: typingInDm || null,
            typing_in_group: typingInGroup || null,
            last_seen: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (fallbackError) {
        console.debug('updateUserPresence fallback upsert error:', fallbackError);
        return false;
      }

      return true;
    }
    return data as boolean;
  } catch (err) {
    console.debug('updateUserPresence exception:', err);
    return false;
  }
};

/**
 * Get users typing in a DM conversation
 */
export const getTypingUsersInDM = async (conversationId: string): Promise<TypingUser[]> => {
  const { data, error } = await supabase.rpc('get_typing_users_in_dm', {
    conversation_id: conversationId,
  });

  if (error) throw error;
  return data as TypingUser[];
};

/**
 * Get users typing in a group
 */
export const getTypingUsersInGroup = async (groupId: string): Promise<TypingUser[]> => {
  const { data, error } = await supabase.rpc('get_typing_users_in_group', {
    group_id: groupId,
  });

  if (error) throw error;
  return data as TypingUser[];
};

/**
 * Get user presence by user ID
 */
export const getUserPresence = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_presence')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found
  return data;
};

/**
 * Get multiple users' presence
 */
export const getUsersPresence = async (userIds: string[]) => {
  const { data, error } = await supabase
    .from('user_presence')
    .select('*')
    .in('user_id', userIds);

  if (error) throw error;
  return data || [];
};

/**
 * Check whether a presence record should be considered "active".
 * Considers explicit `status === 'online'` or a recent `last_seen` timestamp.
 */
export const isUserPresenceActive = (presence: any): boolean => {
  if (!presence) return false;
  try {
    if (presence.status === 'online') return true;
    const lastSeen = presence.last_seen || presence.updated_at || presence.updatedAt || null;
    if (!lastSeen) return false;
    const ts = new Date(lastSeen).getTime();
    if (!Number.isFinite(ts)) return false;
    // Consider active if seen within the last 2 minutes
    return Date.now() - ts <= 2 * 60 * 1000;
  } catch (e) {
    return false;
  }
};

// ============================================
// Attachments (Supabase Storage)
// ============================================

/**
 * Upload an attachment to Supabase Storage
 */
export const uploadAttachment = async (
  file: File,
  conversationId?: string,
  groupId?: string
): Promise<AttachmentUploadResult> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  // Generate unique filename
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(7);
  const extension = file.name.split('.').pop();
  const filename = `${user.user.id}/${timestamp}-${random}.${extension}`;

  // Determine bucket path
  const bucket = 'chat-attachments';
  const path = conversationId
    ? `dm/${conversationId}/${filename}`
    : groupId
    ? `group/${groupId}/${filename}`
    : `misc/${filename}`;

  // Upload to Supabase Storage
  const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error) throw error;

  // Get signed URL (works with private buckets)
  const { data: urlData, error: urlError } = await supabase.storage
    .from(bucket)
    .createSignedUrl(data.path, 60 * 60 * 24 * 365); // 1 year expiry

  if (urlError) throw urlError;

  return {
    url: urlData.signedUrl,
    type: file.type,
    name: file.name,
    size: file.size,
  };
};

/**
 * Delete an attachment from Supabase Storage
 */
export const deleteAttachment = async (attachmentUrl: string): Promise<void> => {
  // Extract path from URL
  const url = new URL(attachmentUrl);
  const pathParts = url.pathname.split('/');
  const bucket = pathParts[pathParts.length - 3]; // Assuming structure: /storage/v1/object/public/{bucket}/{path}
  const path = pathParts.slice(pathParts.length - 2).join('/');

  const { error } = await supabase.storage.from(bucket).remove([path]);

  if (error) throw error;
};

// Ejemplo: Generar signed URL para un archivo específico en el bucket chat-attachments
export const getAttachmentSignedUrl = async (path: string, expiresInSeconds = 3600) => {
  const { data, error } = await supabase
    .storage
    .from('chat-attachments')
    .createSignedUrl(path, expiresInSeconds);
  if (error) throw error;
  return data.signedUrl;
};

// Uso:
// const url = await getAttachmentSignedUrl('dm/ec72e883-30f1-4b38-9729-ab4a7ae8d2f5/eb5f6d80-bf7e-4ba4-a29f-b376ad2adede/1768409623447-q2cvrn.jpeg');

// ============================================
// Notification Preferences
// ============================================

/**
 * Get chat notification preferences
 */
export const getChatNotificationPreferences = async (): Promise<ChatNotificationPreferences | null> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('chat_notification_preferences')
    .select('*')
    .eq('user_id', user.user.id)
    .single();

  if (error && error.code !== 'PGRST116') throw error; // Not found is ok
  return data;
};

/**
 * Update chat notification preferences
 */
export const updateChatNotificationPreferences = async (
  preferences: Partial<Omit<ChatNotificationPreferences, 'id' | 'user_id' | 'updated_at'>>
): Promise<ChatNotificationPreferences> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('chat_notification_preferences')
    .upsert({
      user_id: user.user.id,
      ...preferences,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Mute a conversation
 */
export const muteConversation = async (conversationId: string): Promise<void> => {
  const prefs = await getChatNotificationPreferences();
  const mutedConversations = prefs?.muted_conversations || [];

  if (!mutedConversations.includes(conversationId)) {
    await updateChatNotificationPreferences({
      muted_conversations: [...mutedConversations, conversationId],
    });
  }
};

/**
 * Unmute a conversation
 */
export const unmuteConversation = async (conversationId: string): Promise<void> => {
  const prefs = await getChatNotificationPreferences();
  const mutedConversations = prefs?.muted_conversations || [];

  await updateChatNotificationPreferences({
    muted_conversations: mutedConversations.filter((id) => id !== conversationId),
  });
};

/**
 * Mute a group
 */
export const muteGroup = async (groupId: string): Promise<void> => {
  const prefs = await getChatNotificationPreferences();
  const mutedGroups = prefs?.muted_groups || [];

  if (!mutedGroups.includes(groupId)) {
    await updateChatNotificationPreferences({
      muted_groups: [...mutedGroups, groupId],
    });
  }
};

/**
 * Unmute a group
 */
export const unmuteGroup = async (groupId: string): Promise<void> => {
  const prefs = await getChatNotificationPreferences();
  const mutedGroups = prefs?.muted_groups || [];

  await updateChatNotificationPreferences({
    muted_groups: mutedGroups.filter((id) => id !== groupId),
  });
};

// ============================================// Anonymous Support Chats (for staff/admin)
// ============================================

/**
 * Get all active anonymous support sessions for a store
 */
export const getAnonymousSupportSessions = async (
  storeId = '30e79df7-81e8-4c5f-a825-602e5899d961'
): Promise<any[]> => {
  const { data, error } = await supabase.rpc('get_anonymous_support_chats', {
    p_store_id: storeId,
  });

  if (error) throw error;
  return data || [];
};

/**
 * Mark a legacy anonymous support session as read for current staff user.
 */
export const markAnonymousSupportSessionRead = async (
  anonymousSessionId: string
): Promise<number> => {
  const { data, error } = await supabase.rpc('mark_anonymous_support_session_read', {
    p_anonymous_session_id: anonymousSessionId,
  });

  if (error) throw error;
  return Number(data || 0);
};

// ============================================// Anonymous Support Chats (for non-authenticated users)
// ============================================

/**
 * Save an anonymous support chat message to Supabase
 */
export const saveAnonymousMessage = async (
  anonymousSessionId: string,
  message: string,
  senderType: 'anonymous_user' | 'support_admin' = 'anonymous_user',
  supportStoreId = '30e79df7-81e8-4c5f-a825-602e5899d961'
): Promise<string> => {
  const { data, error } = await supabase
    .from('anonymous_support_chats')
    .insert({
      anonymous_session_id: anonymousSessionId,
      message,
      sender_type: senderType,
      support_store_id: supportStoreId,
      display_name: senderType === 'anonymous_user' ? 'Anónimo' : 'Soporte',
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id as string;
};

/**
 * Get anonymous chat messages for a session
 */
export const getAnonymousMessages = async (
  anonymousSessionId: string
): Promise<any[]> => {
  const { data, error } = await supabase
    .from('anonymous_support_chats')
    .select('*')
    .eq('anonymous_session_id', anonymousSessionId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * DEPRECATED: Use group chats instead
 * Old function for migrating to DM - replaced by group chat migration
 */
